// **The presentation's three modals** (`demo-presentation-shell` task 8.5/8.6, plus chapter 3's
// flowchart view, 2026-08-09). Reacts to
// `prototype/sketches/demo-presentation-shell/api-modal.html`.
//
// The **six-action modal** is a hand-written subset — the requirement names the six words
// verbatim ("exactly the six names the script lists, nothing else") and that is presenter
// content, not parser output, so it is a small local constant rather than something fetched. The
// **full-vocabulary modal** is the opposite on purpose: it renders whatever `DemoVocabEntry[]` it
// is given, grouped by the payload's own `kind` field, so "an added entry in the payload appears
// with no client change" (`client-demo/the-full-vocabulary-modal-renders-from-the-parser-s-
// exported-vocabulary`) is true by construction — there is no name anywhere in this file the
// vocabulary could fail to include.

import type { CSSProperties, ReactElement } from 'react';
import type { DemoStageTypeSpec, DemoVocabEntry, DemoVocabKind } from './stage-driver';
import { BppProgramDiagram } from '../../ui/components/script/BppProgramDiagram';
import { Z } from './z-layers';

const SCRIM_STYLE: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(43, 33, 20, 0.5)',
  zIndex: Z.modalScrim,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const MODAL_STYLE: CSSProperties = {
  background: 'var(--hud-bg)',
  border: '2px solid var(--hud-line)',
  borderRadius: 'var(--r-lg)',
  boxShadow: '0 12px 40px rgba(43, 33, 20, 0.4)',
  fontFamily: 'var(--font-body)',
  display: 'flex',
  flexDirection: 'column',
  maxWidth: '90vw',
};

const HEAD_STYLE: CSSProperties = {
  padding: '26px 38px 22px',
  borderBottom: '1px solid var(--line)',
};

const TITLE_STYLE: CSSProperties = {
  margin: 0,
  fontFamily: 'var(--font-display)',
  fontWeight: 600,
  fontSize: 38,
  color: 'var(--ink-1)',
};

const SUB_STYLE: CSSProperties = {
  margin: '7px 0 0',
  fontSize: 21,
  color: 'var(--ink-3)',
};

/** Exactly the six the script lists, in the script's own order — presenter content, not the
 * parser's vocabulary, so this stays a small hand-written constant (design's own distinction
 * between the two modals; see module header). */
const SIX_ACTIONS: readonly { name: string; kind: 'action' | 'sense' | 'memory' }[] = [
  { name: 'move_to', kind: 'action' },
  { name: 'remember', kind: 'memory' },
  { name: 'collect_nectar', kind: 'action' },
  { name: 'sees_flowers', kind: 'sense' },
  { name: 'return_to_hive', kind: 'action' },
  { name: 'drop_nectar', kind: 'action' },
];

const KIND_CHIP_STYLE: Record<'action' | 'sense' | 'memory', CSSProperties> = {
  action: { color: 'var(--accent-on-soft)', background: 'var(--accent-soft)' },
  sense: { color: '#2C5D7E', background: 'var(--info-soft)' },
  memory: { color: '#4A6B27', background: 'var(--success-soft)' },
};

export function SixActionsModal(): ReactElement {
  return (
    <div data-testid="demo-modal-six-actions" style={SCRIM_STYLE}>
      <div style={{ ...MODAL_STYLE, width: 760 }}>
        <div style={HEAD_STYLE}>
          <h2 style={TITLE_STYLE}>The Apiary Programming Interface</h2>
          <p style={SUB_STYLE}>What a bee can be told to do</p>
        </div>
        <div style={{ padding: '28px 38px 34px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {SIX_ACTIONS.map((entry) => (
            <div
              key={entry.name}
              data-testid="six-action-item"
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 18,
                background: 'var(--surface-card)',
                border: '1.5px solid var(--line-strong)',
                borderRadius: 'var(--r-md)',
                padding: '16px 22px',
                boxShadow: 'var(--shadow-hud)',
              }}
            >
              <code style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, fontSize: 30, color: 'var(--ink-1)' }}>
                {entry.name}
              </code>
              <span
                style={{
                  marginLeft: 'auto',
                  fontSize: 15,
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: 'var(--tracking-caps)',
                  borderRadius: 'var(--r-pill)',
                  padding: '6px 14px',
                  ...KIND_CHIP_STYLE[entry.kind],
                }}
              >
                {entry.kind}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** The kind → group-heading mapping. Purely a display label over `DemoVocabEntry.kind` — never a
 * second source of *which entries exist*, only of what to call the section a given `kind` lands
 * in. An entry whose `kind` this map somehow lacked would still render (falls back to the raw
 * kind string), never get silently dropped. */
const KIND_HEADING: Record<DemoVocabKind, string> = {
  action: 'Actions',
  sense: 'Senses',
  num_prop: 'Self',
  characteristic: 'Characteristics',
  compare_op: 'Comparisons',
  logical_op: 'Logic',
  point: 'Points',
  keyword: 'Structure',
};

const KIND_ORDER: readonly DemoVocabKind[] = [
  'action',
  'sense',
  'num_prop',
  'characteristic',
  'point',
  'keyword',
  'compare_op',
  'logical_op',
];

export interface FullVocabularyModalProps {
  /** `bpp_vocabulary()`'s own payload (contract C-1) — never a client-held list. */
  entries: readonly DemoVocabEntry[];
}

/** Groups by `kind` and renders every entry the payload carries — no vocabulary name is spelled
 * out anywhere in this file (`client-demo/the-full-vocabulary-modal-renders-from-the-parser-s-
 * exported-vocabulary`). Scrolls **inside** the modal body; the presentation surface itself never
 * scrolls (the requirement's own wording). */
export function FullVocabularyModal({ entries }: FullVocabularyModalProps): ReactElement {
  const groups = KIND_ORDER.map((kind) => ({
    kind,
    heading: KIND_HEADING[kind],
    entries: entries.filter((entry) => entry.kind === kind),
  })).filter((group) => group.entries.length > 0);

  return (
    <div data-testid="demo-modal-full-vocabulary" style={SCRIM_STYLE}>
      <div style={{ ...MODAL_STYLE, width: 1280, maxHeight: '82vh' }}>
        <div style={HEAD_STYLE}>
          <h2 style={TITLE_STYLE}>…the whole API</h2>
          <p style={SUB_STYLE}>Every action, sense and operator in B++</p>
        </div>
        <div
          style={{
            padding: '28px 38px 18px',
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '24px 30px',
            overflowY: 'auto',
          }}
        >
          {groups.map((group) => (
            <div key={group.kind}>
              <h3
                style={{
                  margin: '0 0 12px',
                  fontSize: 15,
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: 'var(--tracking-caps)',
                  color: 'var(--ink-3)',
                  borderBottom: '1px solid var(--line)',
                  paddingBottom: 5,
                }}
              >
                {group.heading}
              </h3>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {group.entries.map((entry) => (
                  <li
                    key={entry.name}
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 20,
                      color: 'var(--ink-1)',
                      background: 'var(--paper-1)',
                      border: '1px solid var(--line)',
                      borderRadius: 'var(--r-sm)',
                      padding: '5px 11px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {entry.name}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// --- Chapter 3's flowchart view ---------------------------------------------------------------
//
// "Every B++ program can be drawn as a flowchart — this is the exact program the Beekeeper just
// wrote, as a map." (docs/PRESENTATION.md, "Chapter 3", and its own technical requirement).
//
// The word that does the work in that sentence is **exact**: the beat is worthless if the picture
// is a canned illustration, so this renders the program the agent actually pushed into the world —
// `DemoApp`'s `agentTypes`, which is what `BeekeeperPromptBar` reported after applying the turn.
// It reuses the shared `BppProgramDiagram`: Rust parses the exact source and is the sole AST→graph
// generator, while the shared Mermaid runtime performs layout. There is no live per-state
// histogram behind this view; telemetry remains gap G-B.

export interface BuilderFlowchartModalProps {
  /** The colony's types as the agent left them. The **last** one is the subject: the script's beat
   * is "the program the Beekeeper just wrote", and a created type is appended. */
  types: readonly DemoStageTypeSpec[];
}

export function BuilderFlowchartModal({ types }: BuilderFlowchartModalProps): ReactElement {
  const subject = types.length === 0 ? undefined : types[types.length - 1];

  return (
    <div data-testid="demo-modal-builder-flowchart" style={SCRIM_STYLE}>
      <div style={{ ...MODAL_STYLE, width: 1280, maxHeight: '86vh' }}>
        <div style={HEAD_STYLE}>
          <h2 style={TITLE_STYLE}>{subject === undefined ? 'The B++ program diagram' : subject.name}</h2>
          <p style={SUB_STYLE}>
            States show what a bee is doing; arrows show why it changes its mind.
          </p>
        </div>
        <div
          style={{
            padding: '28px 38px 34px',
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'center',
            overflowY: 'auto',
          }}
        >
          {subject === undefined ? (
            // Nothing to draw yet — the agent's turn has not landed. Says so plainly rather than
            // showing an empty frame the presenter has to explain away.
            <div style={{ fontSize: 24, color: 'var(--ink-3)' }}>
              No program yet — the Beekeeper has not written one.
            </div>
          ) : (
            <BppProgramDiagram
              source={subject.program}
              label={`${subject.name} B++ program diagram`}
              color="var(--bee-builder)"
            />
          )}
        </div>
      </div>
    </div>
  );
}
